import { BPA_SEVERITY_CONFIG, sortBPAProviders } from './config';
import { IntervalFilter, TimespanFilter } from '../../types';
import {
  BPACanonicalCurrentCell,
  BPACanonicalHistorySeries,
  BPACanonicalProvider,
  BPACurrentChartSeries,
  BPADashboardData,
  BPAHistoryBucket,
  BPAHistoryEvent,
  BPAHistorySeries,
  BPATopBenchmarkItem,
  BPATopChartCell,
  BPATopControlItem,
  BPATopGroup,
  BPATopRankings,
} from '../types';

const DURATION_WEEKS: Record<TimespanFilter, number> = {
  [TimespanFilter.OneMonth]: 4,
  [TimespanFilter.ThreeMonth]: 13,
  [TimespanFilter.SixMonth]: 26,
  [TimespanFilter.OneYear]: 52,
};

const INTERVAL_WEEKS: Record<IntervalFilter, number> = {
  [IntervalFilter.Week]: 1,
  [IntervalFilter.FourWeeks]: 4,
  [IntervalFilter.ThreeMonth]: 13,
  [IntervalFilter.SixMonth]: 26,
};

const HISTORY_EVENTS: BPAHistoryEvent[] = ['new', 'fixed', 'ignored'];
const TOP_GROUP_LIMIT = 10;

interface BPAFilterSelection {
  severity: number[];
  provider: string[];
  duration: TimespanFilter;
  interval: IntervalFilter;
}

interface BucketGroup {
  start: number;
  end: number;
}

const sum = (values: number[]) =>
  values.reduce((total, value) => total + value, 0);

export const buildBPACurrentChartSeries = (
  providers: BPACanonicalProvider[],
  currentCells: BPACanonicalCurrentCell[],
  selectedProviders: string[],
  selectedSeverities: number[],
): {
  bySeverity: BPACurrentChartSeries[];
  byEffectiveStatus: BPACurrentChartSeries[];
} => {
  const selectedProviderMetadata = sortBPAProviders(providers).filter(
    (provider) => selectedProviders.includes(provider.provider),
  );

  const series = selectedProviderMetadata.map((provider) => {
    const providerCells = currentCells.filter(
      (cell) => cell.provider === provider.provider,
    );
    const statusCells = providerCells.filter((cell) =>
      selectedSeverities.includes(cell.severity),
    );

    return {
      provider: provider.provider,
      label: provider.label || provider.provider,
      severityCounts: BPA_SEVERITY_CONFIG.map((severity) =>
        sum(
          providerCells
            .filter((cell) => cell.severity === severity.value)
            .map((cell) => cell.active),
        ),
      ),
      statusCounts: [
        sum(statusCells.map((cell) => cell.active)),
        sum(statusCells.map((cell) => cell.fixed)),
        sum(statusCells.map((cell) => cell.ignored)),
      ],
    };
  });

  const toChartSeries = (
    provider: string,
    label: string,
    counts: number[],
  ): BPACurrentChartSeries => ({ provider, label, counts });
  const hasCounts = (item: BPACurrentChartSeries) =>
    item.counts.some((count) => count > 0);

  return {
    bySeverity: series
      .map((item) =>
        toChartSeries(item.provider, item.label, item.severityCounts),
      )
      .filter(hasCounts),
    byEffectiveStatus: series
      .map((item) =>
        toChartSeries(item.provider, item.label, item.statusCounts),
      )
      .filter(hasCounts),
  };
};

const toTopGroup = <T extends { active_count: number }>(
  rows: T[],
): BPATopGroup<T> => ({
  items: rows.slice(0, TOP_GROUP_LIMIT),
  other_count: sum(
    rows.slice(TOP_GROUP_LIMIT).map((item) => item.active_count),
  ),
  total_group_count: rows.length,
});

export const buildBPATopRankings = (
  cells: BPATopChartCell[],
  selectedProviders: string[],
  selectedSeverities: number[],
): BPATopRankings => {
  const filteredCells = cells.filter(
    (cell) =>
      selectedProviders.includes(cell.scope_provider) &&
      selectedSeverities.includes(cell.severity),
  );

  const controlsById = new Map<number, BPATopControlItem>();
  filteredCells.forEach((cell) => {
    const existing = controlsById.get(cell.control_pk);
    if (existing) {
      existing.active_count += cell.active_count;
      return;
    }
    controlsById.set(cell.control_pk, {
      control_pk: cell.control_pk,
      control_id: cell.control_id,
      description: cell.description,
      benchmark_id: cell.benchmark_id,
      benchmark: cell.benchmark,
      benchmark_description: cell.benchmark_description,
      provider: cell.provider,
      severity: cell.severity,
      severity_label: cell.severity_label,
      active_count: cell.active_count,
    });
  });
  const controls = Array.from(controlsById.values()).sort(
    (left, right) =>
      right.active_count - left.active_count ||
      right.severity - left.severity ||
      left.benchmark.localeCompare(right.benchmark) ||
      left.control_id.localeCompare(right.control_id) ||
      left.control_pk - right.control_pk,
  );

  const benchmarksById = new Map<number, BPATopBenchmarkItem>();
  filteredCells.forEach((cell) => {
    const existing = benchmarksById.get(cell.benchmark_id);
    if (existing) {
      existing.active_count += cell.active_count;
      return;
    }
    benchmarksById.set(cell.benchmark_id, {
      benchmark_id: cell.benchmark_id,
      benchmark: cell.benchmark,
      description: cell.benchmark_description,
      provider: cell.provider,
      active_count: cell.active_count,
    });
  });
  const benchmarks = Array.from(benchmarksById.values()).sort(
    (left, right) =>
      right.active_count - left.active_count ||
      left.benchmark.localeCompare(right.benchmark) ||
      left.benchmark_id - right.benchmark_id ||
      left.provider.localeCompare(right.provider),
  );

  return { controls, benchmarks };
};

export const buildBPATopGroups = (
  rankings: BPATopRankings,
): {
  top_controls: BPATopGroup<BPATopControlItem>;
  top_benchmarks: BPATopGroup<BPATopBenchmarkItem>;
} => ({
  top_controls: toTopGroup(rankings.controls),
  top_benchmarks: toTopGroup(rankings.benchmarks),
});

const buildBucketGroups = (
  bucketCount: number,
  durationWeeks: number,
  intervalWeeks: number,
): BucketGroup[] => {
  const durationStart = Math.max(0, bucketCount - durationWeeks);
  const groups: BucketGroup[] = [];
  let end = bucketCount;

  while (end > durationStart) {
    const start = Math.max(durationStart, end - intervalWeeks);
    groups.unshift({ start, end });
    end = start;
  }
  return groups;
};

const aggregateBuckets = (
  buckets: BPAHistoryBucket[],
  groups: BucketGroup[],
  intervalWeeks: number,
): BPAHistoryBucket[] =>
  groups.map(({ start, end }) => {
    const groupedBuckets = buckets.slice(start, end);
    const first = groupedBuckets[0];
    const last = groupedBuckets[groupedBuckets.length - 1];
    return {
      start_at: first.start_at,
      end_at: last.end_at,
      label: last.label,
      is_partial:
        groupedBuckets.length < intervalWeeks ||
        groupedBuckets.some((bucket) => bucket.is_partial),
    };
  });

const combineWeeklyValues = (
  series: BPACanonicalHistorySeries[],
  key: 'open' | BPAHistoryEvent,
  bucketCount: number,
) =>
  Array.from({ length: bucketCount }, (_, index) =>
    sum(series.map((item) => item[key][index] || 0)),
  );

const aggregateValues = (
  weeklyValues: number[],
  groups: BucketGroup[],
  isPopulation: boolean,
) =>
  groups.map(({ start, end }) =>
    isPopulation
      ? weeklyValues[end - 1] || 0
      : sum(weeklyValues.slice(start, end)),
  );

const buildHistorySeries = (
  source: BPADashboardData,
  provider: string | null,
  selectedSeverities: number[],
  groups: BucketGroup[],
  interval: IntervalFilter,
): BPAHistorySeries => {
  const filterData = source.filter_data!;
  const canonicalSeries = filterData.history_series.filter(
    (series) =>
      (provider === null || series.provider === provider) &&
      selectedSeverities.includes(series.severity),
  );
  const bucketCount = source.history.buckets.length;
  const providerMetadata = filterData.providers.find(
    (item) => item.provider === provider,
  );
  const rollingCounts = Object.fromEntries(
    HISTORY_EVENTS.map((event) => [
      event,
      sum(
        canonicalSeries.map(
          (series) => series.rolling_counts_by_interval[interval]?.[event] || 0,
        ),
      ),
    ]),
  ) as Record<BPAHistoryEvent, number>;

  return {
    provider,
    label: provider || 'All Integrations',
    integration_count:
      providerMetadata?.integration_count ||
      (provider === null
        ? sum(filterData.providers.map((item) => item.integration_count))
        : 0),
    open: aggregateValues(
      combineWeeklyValues(canonicalSeries, 'open', bucketCount),
      groups,
      true,
    ),
    new: aggregateValues(
      combineWeeklyValues(canonicalSeries, 'new', bucketCount),
      groups,
      false,
    ),
    fixed: aggregateValues(
      combineWeeklyValues(canonicalSeries, 'fixed', bucketCount),
      groups,
      false,
    ),
    ignored: aggregateValues(
      combineWeeklyValues(canonicalSeries, 'ignored', bucketCount),
      groups,
      false,
    ),
    rolling_counts: rollingCounts,
  };
};

export const applyBPADashboardFilters = (
  source: BPADashboardData,
  selection: BPAFilterSelection,
): BPADashboardData => {
  if (!source.filter_data) return source;

  const selectedSeverities = selection.severity;
  const selectedProvider =
    selection.provider.length === 1 ? selection.provider : [];
  const selectedProviders =
    selectedProvider.length === 1
      ? selectedProvider
      : sortBPAProviders(source.filter_data.providers).map(
          (item) => item.provider,
        );
  const intervalWeeks = INTERVAL_WEEKS[selection.interval];
  const groups = buildBucketGroups(
    source.history.buckets.length,
    DURATION_WEEKS[selection.duration],
    intervalWeeks,
  );
  const buckets = aggregateBuckets(
    source.history.buckets,
    groups,
    intervalWeeks,
  );
  const historySeries = selectedProviders.map((provider) =>
    buildHistorySeries(
      source,
      provider,
      selectedSeverities,
      groups,
      selection.interval,
    ),
  );
  const combinedHistory = Object.fromEntries(
    ['open', ...HISTORY_EVENTS].map((key) => [
      key,
      Array.from({ length: buckets.length }, (_, index) =>
        sum(
          historySeries.map(
            (series) => series[key as 'open' | BPAHistoryEvent][index] || 0,
          ),
        ),
      ),
    ]),
  ) as Pick<BPAHistorySeries, 'open' | 'new' | 'fixed' | 'ignored'>;
  const rollingCounts = Object.fromEntries(
    HISTORY_EVENTS.map((event) => [
      event,
      sum(historySeries.map((series) => series.rolling_counts[event])),
    ]),
  ) as Record<BPAHistoryEvent, number>;

  const providerCurrentCells = source.filter_data.current_cells.filter((cell) =>
    selectedProviders.includes(cell.provider),
  );
  const statusCells = providerCurrentCells.filter((cell) =>
    selectedSeverities.includes(cell.severity),
  );
  const bySeverity = BPA_SEVERITY_CONFIG.map((severity) => ({
    severity: severity.value,
    label: severity.label,
    count: sum(
      providerCurrentCells
        .filter((cell) => cell.severity === severity.value)
        .map((cell) => cell.active),
    ),
  }));
  const byEffectiveStatus = [
    { label: 'Active', count: sum(statusCells.map((cell) => cell.active)) },
    { label: 'Fixed', count: sum(statusCells.map((cell) => cell.fixed)) },
    { label: 'Ignored', count: sum(statusCells.map((cell) => cell.ignored)) },
  ];
  const currentChartSeries = buildBPACurrentChartSeries(
    source.filter_data.providers,
    source.filter_data.current_cells,
    selectedProviders,
    selectedSeverities,
  );
  const integrationCount = sum(
    source.filter_data.providers
      .filter((item) => selectedProviders.includes(item.provider))
      .map((item) => item.integration_count),
  );
  const topGroups = buildBPATopGroups(
    buildBPATopRankings(
      source.filter_data.top_chart_cells || [],
      selectedProviders,
      selectedSeverities,
    ),
  );

  return {
    ...source,
    scope: {
      ...source.scope,
      severity: selection.severity,
      provider: selectedProvider,
      integration_count: integrationCount,
    },
    history: {
      duration: selection.duration,
      interval: selection.interval,
      current_week_start_at:
        source.history.current_week_start_at ||
        source.history.buckets[source.history.buckets.length - 1]?.start_at,
      buckets,
      ...combinedHistory,
      rolling_counts: rollingCounts,
      series: historySeries,
    },
    current: {
      by_severity: bySeverity,
      by_effective_status: byEffectiveStatus,
      by_severity_series: currentChartSeries.bySeverity,
      by_effective_status_series: currentChartSeries.byEffectiveStatus,
      ...topGroups,
    },
  };
};
